import * as React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
    TimesIcon,
    MapMarkerIcon,
    SearchIcon,
    AdjustIcon,
    ExpandIcon,
    CompressIcon,
    CrosshairsIcon
} from '@patternfly/react-icons';
import {
    GoogleMap,
    LoadScript,
    Marker,
    Circle,
    Autocomplete
} from '@react-google-maps/api';
import {
    GeoFenceLocation,
    DEFAULT_LOCATION,
    DEFAULT_RADIUS,
    MIN_RADIUS,
    MAX_RADIUS
} from './types/geoFence';
import { SOCAL_RESTRICTION, SOCAL_AUTOCOMPLETE_BOUNDS, isWithinSoCalBounds } from './types/mapConstants';

// Google Maps libraries to load
const libraries: ("places")[] = ["places"];

// Get API key from environment
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

export interface IGeoFenceMapModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (location: GeoFenceLocation) => void;
    initialLocation?: GeoFenceLocation;
}

const GeoFenceMapModal: React.FunctionComponent<IGeoFenceMapModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    initialLocation
}) => {
    const [address, setAddress] = useState(initialLocation ? initialLocation.address : '');
    const [latitude, setLatitude] = useState(initialLocation ? initialLocation.latitude : DEFAULT_LOCATION.latitude);
    const [longitude, setLongitude] = useState(initialLocation ? initialLocation.longitude : DEFAULT_LOCATION.longitude);
    const [radius, setRadius] = useState(initialLocation ? initialLocation.radius : DEFAULT_RADIUS);
    const [mapClicked, setMapClicked] = useState(!!initialLocation);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const [searchInput, setSearchInput] = useState('');
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLocating, setIsLocating] = useState(false);

    const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Reset state when modal opens with new initial values
    useEffect(() => {
        if (isOpen) {
            if (initialLocation) {
                setAddress(initialLocation.address || '');
                setSearchInput(initialLocation.address || '');
                setLatitude(initialLocation.latitude);
                setLongitude(initialLocation.longitude);
                setRadius(initialLocation.radius);
                setMapClicked(true);
            } else {
                setAddress('');
                setSearchInput('');
                setLatitude(DEFAULT_LOCATION.latitude);
                setLongitude(DEFAULT_LOCATION.longitude);
                setRadius(DEFAULT_RADIUS);
                setMapClicked(false);
            }
            setIsFullscreen(false);
        }
    }, [isOpen, initialLocation]);

    // Handle map click to place marker
    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            if (!isWithinSoCalBounds(lat, lng)) return;
            setLatitude(lat);
            setLongitude(lng);
            setMapClicked(true);

            // Reverse geocode to get address
            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                if (status === 'OK' && results && results[0]) {
                    const formattedAddress = results[0].formatted_address;
                    setAddress(formattedAddress);
                    setSearchInput(formattedAddress);
                } else {
                    const coordsAddress = lat.toFixed(6) + ', ' + lng.toFixed(6);
                    setAddress(coordsAddress);
                    setSearchInput(coordsAddress);
                }
            });
        }
    }, []);

    // Handle place selection from autocomplete
    const handlePlaceChanged = useCallback(() => {
        if (autocompleteRef.current) {
            const place = autocompleteRef.current.getPlace();
            if (place.geometry && place.geometry.location) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                const formattedAddress = place.formatted_address || place.name || '';
                setLatitude(lat);
                setLongitude(lng);
                setAddress(formattedAddress);
                setSearchInput(formattedAddress);
                setMapClicked(true);
            }
        }
    }, []);

    // Handle manual address search (when user types and presses Enter)
    const handleAddressSearch = useCallback((searchAddress: string) => {
        if (!searchAddress.trim()) return;

        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ address: searchAddress }, (results, status) => {
            if (status === 'OK' && results && results[0] && results[0].geometry) {
                const location = results[0].geometry.location;
                const lat = location.lat();
                const lng = location.lng();
                const formattedAddress = results[0].formatted_address || searchAddress;
                setLatitude(lat);
                setLongitude(lng);
                setAddress(formattedAddress);
                setSearchInput(formattedAddress);
                setMapClicked(true);
            }
        });
    }, []);

    // Handle Enter key in search input
    const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddressSearch(searchInput);
        }
    }, [searchInput, handleAddressSearch]);

    // Use browser geolocation to set location
    const handleUseCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) return;
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                if (!isWithinSoCalBounds(lat, lng)) {
                    console.warn('Current location is outside Southern California');
                    setIsLocating(false);
                    return;
                }
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ location: { lat, lng } }, (results, status) => {
                    const formattedAddress = (status === 'OK' && results && results[0])
                        ? results[0].formatted_address
                        : `${lat.toFixed(6)}, ${lng.toFixed(6)}`;

                    setLatitude(lat);
                    setLongitude(lng);
                    setAddress(formattedAddress);
                    setSearchInput(formattedAddress);
                    setMapClicked(true);

                    if (map) {
                        map.panTo({ lat, lng });
                        map.setZoom(16);
                    }
                    setIsLocating(false);
                });
            },
            (error) => {
                console.warn('Geolocation error:', error.message);
                setIsLocating(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    }, [map]);

    // Pan map when location changes
    useEffect(() => {
        if (map && mapClicked) {
            map.panTo({ lat: latitude, lng: longitude });
        }
    }, [map, latitude, longitude, mapClicked]);

    const handleConfirm = () => {
        if (mapClicked && address) {
            onConfirm({
                latitude,
                longitude,
                address,
                radius
            });
        }
    };

    const canConfirm = mapClicked && address.trim() !== '';

    // Map container style - adjusts based on fullscreen mode
    const mapContainerStyle = {
        width: '100%',
        height: isFullscreen ? 'calc(100vh - 300px)' : '400px',
        minHeight: isFullscreen ? '400px' : '400px',
        borderRadius: '12px'
    };

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.55)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: isFullscreen ? '0' : '24px',
            boxSizing: 'border-box' as const
        },
        modal: {
            background: '#ffffff',
            borderRadius: isFullscreen ? 0 : 20,
            width: '100%',
            maxWidth: isFullscreen ? '100%' : '760px',
            height: isFullscreen ? '100vh' : 'auto',
            maxHeight: isFullscreen ? '100vh' : '92vh',
            overflow: 'auto',
            boxShadow: isFullscreen ? 'none' : '0 24px 64px rgba(15, 23, 42, 0.25)'
        },
        header: {
            background: '#ffffff',
            padding: '16px 24px',
            position: 'relative' as const,
            borderRadius: isFullscreen ? '0' : '20px 20px 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #e5e7eb',
            minHeight: '52px'
        },
        headerActions: {
            display: 'flex',
            gap: '6px'
        },
        iconButton: {
            background: '#f3f4f6',
            border: 'none',
            borderRadius: '8px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#6b7280',
            transition: 'background 0.15s'
        },
        title: {
            color: '#0f172a',
            fontSize: '17px',
            fontWeight: 700,
            margin: 0,
            letterSpacing: '-0.3px'
        },
        body: {
            padding: '24px'
        },
        searchContainer: {
            position: 'absolute' as const,
            top: '12px',
            left: '12px',
            right: '12px',
            zIndex: 10
        },
        searchInputWrapper: {
            position: 'relative' as const
        },
        searchInput: {
            width: '100%',
            padding: '12px 42px 12px 40px',
            fontSize: '14px',
            border: 'none',
            borderRadius: '8px',
            outline: 'none',
            backgroundColor: '#fff',
            boxSizing: 'border-box' as const,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
        },
        searchIcon: {
            position: 'absolute' as const,
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#5f6368',
            zIndex: 1
        },
        locationButton: {
            position: 'absolute' as const,
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#5f6368',
            padding: '8px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.2s',
            zIndex: 1
        },
        mapContainer: {
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '20px',
            border: '1px solid #e5e7eb',
            position: 'relative' as const
        },
        radiusSection: {
            marginBottom: '20px'
        },
        radiusLabel: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px'
        },
        radiusTitle: {
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        },
        radiusValue: {
            fontSize: '13px',
            fontWeight: 700,
            color: '#6366f1',
            backgroundColor: '#f5f3ff',
            padding: '4px 12px',
            borderRadius: '9999px',
            border: '1px solid #ddd6fe'
        },
        slider: {
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            appearance: 'none' as const,
            background: '#e5e7eb',
            outline: 'none',
            cursor: 'pointer'
        },
        sliderLabels: {
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            fontSize: '11px',
            color: '#9ca3af'
        },
        coordsDisplay: {
            display: 'flex',
            gap: '16px',
            marginBottom: '20px',
            padding: '12px 16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            fontSize: '13px'
        },
        coordItem: {
            display: 'flex',
            gap: '8px'
        },
        coordLabel: {
            color: '#6b7280',
            fontWeight: 500
        },
        coordValue: {
            color: '#1f2937',
            fontWeight: 600,
            fontFamily: 'monospace'
        },
        footer: {
            padding: '16px 24px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            borderTop: '1px solid #e5e7eb'
        },
        cancelButton: {
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            color: '#111827',
            padding: '12px 22px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s'
        },
        confirmButton: {
            background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
            border: 'none',
            color: '#ffffff',
            padding: '12px 22px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            cursor: 'pointer',
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            transition: 'all 0.15s'
        },
        confirmButtonDisabled: {
            background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
            border: 'none',
            color: '#ffffff',
            padding: '12px 22px',
            fontSize: '14px',
            fontWeight: 600,
            borderRadius: '10px',
            cursor: 'not-allowed',
            boxShadow: 'none'
        },
        loadingContainer: {
            width: '100%',
            height: isFullscreen ? 'calc(100vh - 300px)' : '400px',
            minHeight: isFullscreen ? '400px' : '400px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f3f4f6',
            borderRadius: '12px'
        },
        errorNotice: {
            backgroundColor: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#dc2626'
        }
    };

    if (!isOpen) return null;

    // Check if API key is configured
    const hasApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 0;

    return (
        <div style={styles.overlay} onClick={onClose}>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Select geo fence location</h1>
                    <div style={styles.headerActions}>
                        <button
                            style={styles.iconButton}
                            onClick={() => setIsFullscreen(!isFullscreen)}
                            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                            title={isFullscreen ? "Exit fullscreen" : "Expand to fullscreen"}
                            type="button"
                        >
                            {isFullscreen ? <CompressIcon /> : <ExpandIcon />}
                        </button>
                        <button
                            style={styles.iconButton}
                            onClick={onClose}
                            aria-label="Close"
                            type="button"
                        >
                            <TimesIcon />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={styles.body}>
                    {!hasApiKey ? (
                        <div style={styles.errorNotice}>
                            <strong>Configuration Required:</strong> Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file and restart the development server.
                        </div>
                    ) : (
                        <LoadScript
                            googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                            libraries={libraries}
                        >
                            {/* Map with overlaid search */}
                            <div style={styles.mapContainer}>
                                {/* Address Search - overlaid on map */}
                                <div style={styles.searchContainer}>
                                    <div style={styles.searchInputWrapper}>
                                        <div style={styles.searchIcon}>
                                            <SearchIcon />
                                        </div>
                                        <Autocomplete
                                            onLoad={(autocomplete) => {
                                                autocompleteRef.current = autocomplete;
                                                autocomplete.setFields(['formatted_address', 'geometry', 'name']);
                                                autocomplete.setBounds(SOCAL_AUTOCOMPLETE_BOUNDS);
                                                autocomplete.setOptions({ strictBounds: true });
                                            }}
                                            onPlaceChanged={handlePlaceChanged}
                                        >
                                            <input
                                                ref={inputRef}
                                                type="text"
                                                placeholder="Search for an address..."
                                                style={styles.searchInput}
                                                value={searchInput}
                                                onChange={(e) => setSearchInput(e.target.value)}
                                                onKeyDown={handleSearchKeyDown}
                                            />
                                        </Autocomplete>
                                        <button
                                            style={{
                                                ...styles.locationButton,
                                                ...(isLocating ? { color: '#10b981', cursor: 'wait' } : {})
                                            }}
                                            onClick={handleUseCurrentLocation}
                                            disabled={isLocating}
                                            title="Use my current location"
                                            aria-label="Use my current location"
                                            type="button"
                                        >
                                            {isLocating ? (
                                                <span style={{
                                                    display: 'inline-block',
                                                    width: '16px',
                                                    height: '16px',
                                                    border: '2px solid #10b981',
                                                    borderTopColor: 'transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 0.8s linear infinite'
                                                }} />
                                            ) : (
                                                <CrosshairsIcon />
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <GoogleMap
                                    mapContainerStyle={mapContainerStyle}
                                    center={{ lat: latitude, lng: longitude }}
                                    zoom={14}
                                    onClick={handleMapClick}
                                    onLoad={(mapInstance) => setMap(mapInstance)}
                                    options={{
                                        streetViewControl: false,
                                        mapTypeControl: false,
                                        fullscreenControl: false,
                                        restriction: SOCAL_RESTRICTION
                                    }}
                                >
                                    {mapClicked && (
                                        <>
                                            <Marker
                                                position={{ lat: latitude, lng: longitude }}
                                            />
                                            <Circle
                                                center={{ lat: latitude, lng: longitude }}
                                                radius={radius}
                                                options={{
                                                    fillColor: '#10b981',
                                                    fillOpacity: 0.2,
                                                    strokeColor: '#059669',
                                                    strokeWeight: 2
                                                }}
                                            />
                                        </>
                                    )}
                                </GoogleMap>
                            </div>
                        </LoadScript>
                    )}

                    {/* Coordinates Display */}
                    {mapClicked && (
                        <div style={styles.coordsDisplay}>
                            <div style={styles.coordItem}>
                                <span style={styles.coordLabel}>Lat:</span>
                                <span style={styles.coordValue}>{latitude.toFixed(6)}</span>
                            </div>
                            <div style={styles.coordItem}>
                                <span style={styles.coordLabel}>Lng:</span>
                                <span style={styles.coordValue}>{longitude.toFixed(6)}</span>
                            </div>
                            <div style={styles.coordItem}>
                                <span style={styles.coordLabel}>Address:</span>
                                <span style={{...styles.coordValue, fontFamily: 'inherit', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{address}</span>
                            </div>
                        </div>
                    )}

                    {/* Radius Slider */}
                    <div style={styles.radiusSection}>
                        <div style={styles.radiusLabel}>
                            <span style={styles.radiusTitle}>
                                <AdjustIcon /> Geo Fence Radius
                            </span>
                            <span style={styles.radiusValue}>{radius}m</span>
                        </div>
                        <input
                            type="range"
                            min={MIN_RADIUS}
                            max={MAX_RADIUS}
                            value={radius}
                            onChange={(e) => setRadius(Number(e.target.value))}
                            style={styles.slider}
                        />
                        <div style={styles.sliderLabels}>
                            <span>{MIN_RADIUS}m</span>
                            <span>{MAX_RADIUS / 2}m</span>
                            <span>{MAX_RADIUS}m</span>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.footer}>
                    <button style={styles.cancelButton} onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        style={canConfirm ? styles.confirmButton : styles.confirmButtonDisabled}
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                    >
                        Confirm Location
                    </button>
                </div>
            </div>
        </div>
    );
};

export { GeoFenceMapModal };
